// AiCandidateList — list of `AiCandidateRow`s with empty / loading / error
// state plus the missing-trust banner row required by the WP-9 design rule
// ("AI output never claims trust without provenance").
//
// The list is used by the new `AIReviewToolCard` and is shaped so the
// existing `TwinProductUx.SemanticPropertyCard` and the global
// `CopilotDock` can also adopt it in a follow-up PR (the masterplan §8.9
// makes this the long-term path).

import type { CandidateFact } from "@/app/scenePackage";
import { Banner, Spinner, Stack, Text, tokens } from "@/design";
import { AiCandidateRow } from "./AiCandidateRow";
import {
  partitionCandidates,
  type AiCandidateRowVM,
} from "./aiCandidateAdapter";

export interface AiCandidateListProps {
  readonly candidates: readonly CandidateFact[];
  readonly isLoading?: boolean;
  readonly error?: string | null;
  readonly disabled?: boolean;
  readonly busyCandidateIds?: ReadonlySet<string>;
  readonly statusOverrides?: Readonly<
    Record<string, CandidateFact["reviewStatus"]>
  >;
  readonly onApprove: (candidateId: string) => void;
  readonly onReject: (candidateId: string) => void;
  readonly onRetry?: () => void;
  readonly emptyMessage?: string;
}

export function AiCandidateList({
  candidates,
  isLoading,
  error,
  disabled,
  busyCandidateIds,
  statusOverrides,
  onApprove,
  onReject,
  onRetry,
  emptyMessage,
}: AiCandidateListProps) {
  if (isLoading) {
    return (
      <Stack
        direction="row"
        align="center"
        gap={2}
        data-testid="ai-candidate-list-loading"
        style={{
          padding: tokens.space[3],
          color: tokens.color.ink.secondary,
        }}
      >
        <Spinner size="sm" aria-label="Loading AI candidates" />
        <Text variant="caption" as="span">
          Loading AI candidates…
        </Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <div data-testid="ai-candidate-list-error">
        <Banner
          severity="error"
          title="Failed to load AI candidates"
          body={error}
          actions={onRetry ? [{ label: "Retry", onClick: onRetry }] : undefined}
        />
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div
        data-testid="ai-candidate-list-empty"
        style={{
          padding: tokens.space[3],
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          background: tokens.color.surface[2],
          color: tokens.color.ink.secondary,
        }}
      >
        <Text variant="caption" as="p" style={{ margin: 0 }}>
          {emptyMessage ??
            "No AI or rule-inferred candidate facts attached to this layer."}
        </Text>
      </div>
    );
  }

  const partitioned = partitionCandidates(candidates);

  return (
    <ul
      data-testid="ai-candidate-list"
      aria-label="AI candidate facts"
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: tokens.space[1],
      }}
    >
      {partitioned.missingTrust.map((vm) => (
        <li
          key={`missing-trust:${vm.id}`}
          data-testid={`ai-candidate-missing-trust-${vm.id}`}
          style={{ listStyle: "none" }}
        >
          <Banner
            severity="warning"
            title="Candidate is missing trust signals"
            body={`Candidate ${vm.id} on ${vm.subjectNodeId} cannot display confidence and source attribution; review the producer before relying on this fact.`}
            errorCode="AI-CANDIDATE-MISSING-TRUST"
          />
        </li>
      ))}
      {partitioned.rows.map((vm) => (
        <AiCandidateRow
          key={vm.id}
          vm={vm}
          overrideStatus={statusOverrides?.[vm.id]}
          disabled={disabled}
          busy={busyCandidateIds?.has(vm.id)}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </ul>
  );
}

/** Convenience: derive the partitioned VM directly from a candidate
 *  array. Exposed so the card header can show counters. */
export function deriveListCounts(candidates: readonly CandidateFact[]): {
  readonly total: number;
  readonly pending: number;
  readonly missingTrust: number;
  readonly rows: readonly AiCandidateRowVM[];
} {
  const partitioned = partitionCandidates(candidates);
  const pending = partitioned.rows.filter(
    (row) => row.reviewStatus === "pending",
  ).length;
  return {
    total: candidates.length,
    pending,
    missingTrust: partitioned.missingTrust.length,
    rows: partitioned.rows,
  };
}
